import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Divider,
    Chip,
    Tooltip,
    IconButton,
    Paper,
    Fade
} from '@mui/material';
import {
    ArrowBackIos,
    ArrowForwardIos,
    FitnessCenter as FitnessCenterIcon,
    CheckCircleRounded as CheckCircleRoundedIcon,
    AutoAwesome as AutoAwesomeIcon,
    LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { formatRegistrationScheduleForDisplay } from "../utils/planSchedule";
import { formatCurrency } from "../utils/currency";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    textAlign: "center",
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    transition: "0.3s",
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[2],
    position: "relative",
    overflow: "hidden",
    "&:hover": {
        boxShadow: theme.shadows[5],
        transform: "translateY(-4px)",
    },
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
    boxShadow: theme.shadows[1],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '500px',
}));

const CarouselContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
        maxWidth: '400px',
        margin: '0 auto',
    },
    [theme.breakpoints.up('md')]: {
        maxWidth: '1200px',
        gap: theme.spacing(3),
        padding: theme.spacing(0, 2),
    },
}));

const PlanCard = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '320px',
        margin: '0 auto',
    },
    [theme.breakpoints.up('md')]: {
        width: '100%',
        maxWidth: '350px',
        transition: 'all 0.3s ease',
        '&.side-plan': {
            transform: 'scale(0.85)',
            opacity: 0.7,
            filter: 'grayscale(20%)',
        },
        '&.center-plan': {
            transform: 'scale(1)',
            opacity: 1,
            filter: 'none',
            zIndex: 2,
        },
    },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[3],
    zIndex: 10,
    width: 48,
    height: 48,
    '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        transform: 'translateY(-50%) scale(1.1)',
    },
    transition: 'all 0.2s ease',
    [theme.breakpoints.down('sm')]: {
        width: 40,
        height: 40,
    },
}));

const PromoTag = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#FF6B35',
    color: 'white',
    padding: theme.spacing(0.5, 2),
    fontSize: '0.75rem',
    fontWeight: 'bold',
    transform: 'rotate(45deg) translate(25%, -50%)',
    transformOrigin: 'center',
    zIndex: 3,
    minWidth: '80px',
    textAlign: 'center',
    boxShadow: theme.shadows[2],
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        height: '4px',
        backgroundColor: '#E55A2B',
    }
}));

const trainingTypeColors = {
    "All classes": "success",
    "WOD": "primary",
    "Weightlifting": "secondary",
    "Cardio": "error",
    "Rowing": "info",
    "Gymnastics": "warning",
    "Open Gym": "default",
    "Other": "info"
};

const TrainingTypeChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
    fontWeight: 500,
}));

const ensureTrainingTypeArray = (trainingType) => {
    if (!trainingType) return [];
    if (Array.isArray(trainingType)) return trainingType;
    if (typeof trainingType === 'string') {
        try {
            const parsed = JSON.parse(trainingType);
            return Array.isArray(parsed) ? parsed : [trainingType];
        } catch (e) {
            return [trainingType];
        }
    }
    return [trainingType];
};

const ShowPlans = ({ plans, userPlans, onBuyPlan, getValidUntil, paymentCurrency = "EUR" }) => {
    const [galleryGroups, setGalleryGroups] = useState([]);
    const [standalonePlans, setStandalonePlans] = useState({});
    const [carouselIndices, setCarouselIndices] = useState({});

    useEffect(() => {
        organizePlans();
    }, [plans]);

    const organizePlans = () => {
        const groups = [];
        const standalone = [];
        const processedPlanIds = new Set();
        const indices = {};

        // Esiteks leia kõik galerii pealplaanid
        const galleryMainPlans = plans.filter(plan => {
            if (!plan.galleryPlans) return false;

            let galleryPlanIds = [];
            if (typeof plan.galleryPlans === 'string') {
                try {
                    galleryPlanIds = JSON.parse(plan.galleryPlans);
                } catch (e) {
                    return false;
                }
            }

            return galleryPlanIds.length > 0;
        });

        // Töötle galerii pealplaanid
        galleryMainPlans.forEach(plan => {
            if (processedPlanIds.has(plan.id)) return;

            let galleryPlanIds = [];
            if (typeof plan.galleryPlans === 'string') {
                try {
                    galleryPlanIds = JSON.parse(plan.galleryPlans);
                } catch (e) {
                    return;
                }
            }

            const validGalleryPlans = plans.filter(p =>
                galleryPlanIds.includes(p.id) &&
                !processedPlanIds.has(p.id)
            );

            if (validGalleryPlans.length > 0) {
                const groupId = `gallery-${plan.id}`;
                const group = {
                    id: groupId,
                    mainPlan: plan,
                    plans: [plan, ...validGalleryPlans],
                    isGallery: true
                };

                groups.push(group);
                indices[groupId] = 0;
                processedPlanIds.add(plan.id);
                validGalleryPlans.forEach(p => processedPlanIds.add(p.id));
            } else {
                standalone.push(plan);
                processedPlanIds.add(plan.id);
            }
        });

        // Seejärel töötle ülejäänud plaanid
        plans.forEach(plan => {
            if (processedPlanIds.has(plan.id)) return;
            standalone.push(plan);
            processedPlanIds.add(plan.id);
        });

        // Sorteeri gallery grupid - need, mis sisaldavad special price plaane, tulevad esimesena
        const sortedGalleryGroups = groups.sort((a, b) => {
            const aHasSpecialPrice = a.plans.some(plan => plan.isSpecialPrice);
            const bHasSpecialPrice = b.plans.some(plan => plan.isSpecialPrice);

            // Kui üks sisaldab special price ja teine mitte
            if (aHasSpecialPrice && !bHasSpecialPrice) return -1;
            if (!aHasSpecialPrice && bHasSpecialPrice) return 1;

            // Kui mõlemad sisaldavad või ei sisalda special price, jäävad samasse järjekorda
            return 0;
        });

        // Sorteeri standalone plaanid - special price plaanid esimesena, seejärel hinna järgi
        const sortedStandalone = standalone.sort((a, b) => {
            // Kui üks on special price ja teine mitte
            if (a.isSpecialPrice && !b.isSpecialPrice) return -1;
            if (!a.isSpecialPrice && b.isSpecialPrice) return 1;

            // Kui mõlemad on special price või kumbki pole, sorteeri hinna järgi
            return parseFloat(a.price || 0) - parseFloat(b.price || 0);
        });

        // Jaota standalone plaanid special price ja tavalisteks
        const specialPriceStandalone = sortedStandalone.filter(plan => plan.isSpecialPrice);
        const regularStandalone = sortedStandalone.filter(plan => !plan.isSpecialPrice);

        setGalleryGroups(sortedGalleryGroups);
        setStandalonePlans({ specialPrice: specialPriceStandalone, regular: regularStandalone });
        setCarouselIndices(indices);
    };

    const getPlanDescription = (plan) => {
        if (plan.sessions === 9999) {
            return "Unlimited sessions";
        } else {
            return `${plan.sessions} sessions`;
        }
    };

    const getPlanValidityText = (plan) => {
        if (plan.singleCalendarDayValidity) {
            return "1 calendar day";
        }

        if (plan.exactMonthValidity) {
            return "1 month";
        }
        return `${plan.validityDays} days`;
    };

    const updateCarouselIndex = (groupId, newIndex) => {
        setCarouselIndices(prev => ({
            ...prev,
            [groupId]: newIndex
        }));
    };

    const renderPlanCard = (plan) => {
        const validUntil = getValidUntil(plan.id, userPlans);
        const isPremium = plan.name === "Professional" || plan.name.toLowerCase().includes("premium");
        const isSpecialPrice = plan.isSpecialPrice;
        const scheduleLines = formatRegistrationScheduleForDisplay(
            plan.hasRegistrationSchedule,
            plan.registrationSchedule
        );

        return (
            <StyledCard
                sx={isPremium ? {
                    border: "none",
                    boxShadow: "0 8px 12px hsla(220, 20%, 42%, 0.2)",
                    bgcolor: "background.paper",
                    borderLeft: "5px solid #FFB347",
                } : {}}
            >
                {/* Special Price Promo Tag */}
                {isSpecialPrice && (
                    <PromoTag>
                        PROMO
                    </PromoTag>
                )}

                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ mb: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 2, position: "relative" }}>
                        <Typography component="h3" variant="h4" fontWeight="bold" textAlign="center">
                            {plan.name}
                        </Typography>
                        {isPremium && (
                            <AutoAwesomeIcon
                                color="warning"
                                sx={{
                                    position: "absolute",
                                    right: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)"
                                }}
                            />
                        )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", mb: 2 }}>
                        <Typography component="h3" variant="h5" fontWeight="bold">
                            {formatCurrency(plan.price, paymentCurrency)}
                        </Typography>
                        {isSpecialPrice && (
                            <Chip
                                icon={<LocalOfferIcon />}
                                label="Special Price"
                                color="error"
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Box>

                    <Divider sx={{ my: 2, opacity: 0.8, borderColor: "divider" }} />

                    <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                        <CheckCircleRoundedIcon color="primary" />
                        <Typography variant="body1">{getPlanValidityText(plan)}</Typography>
                    </Box>

                    <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                        <CheckCircleRoundedIcon color="primary" />
                        <Typography variant="body1">{getPlanDescription(plan)}</Typography>
                    </Box>

                    {scheduleLines.length > 0 && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                                Registration schedule:
                            </Typography>
                            {scheduleLines.map((line) => (
                                <Typography key={line} variant="body2" color="text.primary">
                                    {line}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: "medium" }}>
                            <FitnessCenterIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
                            Included Training Types:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", minHeight: "70px" }}>
                            {Array.isArray(plan.trainingType) && plan.trainingType.length > 0 ? (
                                ensureTrainingTypeArray(plan.trainingType).map((type, index) => (
                                    <Tooltip title={type} key={index}>
                                        <TrainingTypeChip
                                            label={type}
                                            color={trainingTypeColors[type] || "default"}
                                            size="small"
                                        />
                                    </Tooltip>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No training types specified
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {validUntil && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                            <Typography variant="body1" color="primary" sx={{ fontWeight: "medium" }}>
                                Already active until {validUntil}
                            </Typography>
                        </Box>
                    )}

                    {plan.additionalData && (
                        <Box sx={{ mt: "auto", mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {plan.additionalData}
                            </Typography>
                        </Box>
                    )}
                </CardContent>

                <CardActions>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => onBuyPlan(plan)}
                        sx={{
                            borderRadius: "20px",
                            py: 1,
                        }}
                    >
                        Buy Plan
                    </Button>
                </CardActions>
            </StyledCard>
        );
    };

    const renderCarousel = (group) => {
        const currentIndex = carouselIndices[group.id] || 0;
        const totalPlans = group.plans.length;

        const nextSlide = () => {
            const newIndex = (currentIndex + 1) % totalPlans;
            updateCarouselIndex(group.id, newIndex);
        };

        const prevSlide = () => {
            const newIndex = (currentIndex - 1 + totalPlans) % totalPlans;
            updateCarouselIndex(group.id, newIndex);
        };

        const setSlideIndex = (index) => {
            updateCarouselIndex(group.id, index);
        };

        const getVisiblePlans = () => {
            const isMobile = window.innerWidth < 900;

            if (isMobile) {
                return [{
                    plan: group.plans[currentIndex],
                    position: 'center'
                }];
            } else {
                const visiblePlans = [];

                if (totalPlans === 1) {
                    visiblePlans.push({
                        plan: group.plans[0],
                        position: 'center'
                    });
                } else if (totalPlans === 2) {
                    visiblePlans.push({
                        plan: group.plans[currentIndex],
                        position: 'center'
                    });
                    visiblePlans.push({
                        plan: group.plans[(currentIndex + 1) % totalPlans],
                        position: 'right'
                    });
                } else {
                    const leftIndex = (currentIndex - 1 + totalPlans) % totalPlans;
                    const rightIndex = (currentIndex + 1) % totalPlans;

                    visiblePlans.push({
                        plan: group.plans[leftIndex],
                        position: 'left'
                    });
                    visiblePlans.push({
                        plan: group.plans[currentIndex],
                        position: 'center'
                    });
                    visiblePlans.push({
                        plan: group.plans[rightIndex],
                        position: 'right'
                    });
                }

                return visiblePlans;
            }
        };

        const visiblePlans = getVisiblePlans();
        const isMobile = window.innerWidth < 900;

        return (
            <Box key={group.id} sx={{ mb: 6 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                        Plan Gallery
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {totalPlans} plans in this collection
                    </Typography>
                </Box>

                <CarouselContainer>
                    <CarouselContent>
                        {totalPlans > 1 && (
                            <NavigationButton
                                onClick={prevSlide}
                                sx={{
                                    left: { xs: 10, sm: 20 },
                                    zIndex: 10
                                }}
                            >
                                <ArrowBackIos />
                            </NavigationButton>
                        )}

                        {isMobile ? (
                            <PlanCard>
                                <Fade in={true} key={visiblePlans[0].plan.id}>
                                    <Box>
                                        {renderPlanCard(visiblePlans[0].plan)}
                                    </Box>
                                </Fade>
                            </PlanCard>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 3,
                                width: '100%'
                            }}>
                                {visiblePlans.map((item, index) => (
                                    <PlanCard
                                        key={`${item.plan.id}-${index}`}
                                        className={item.position === 'center' ? 'center-plan' : 'side-plan'}
                                    >
                                        {renderPlanCard(item.plan)}
                                    </PlanCard>
                                ))}
                            </Box>
                        )}

                        {totalPlans > 1 && (
                            <NavigationButton
                                onClick={nextSlide}
                                sx={{
                                    right: { xs: 10, sm: 20 },
                                    zIndex: 10
                                }}
                            >
                                <ArrowForwardIos />
                            </NavigationButton>
                        )}
                    </CarouselContent>

                    {totalPlans > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            bottom: { xs: 8, md: 12 },
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            gap: 1
                        }}>
                            {group.plans.map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: index === currentIndex ? 'primary.main' : 'grey.300',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'primary.main',
                                            transform: 'scale(1.2)'
                                        }
                                    }}
                                    onClick={() => setSlideIndex(index)}
                                />
                            ))}
                        </Box>
                    )}
                </CarouselContainer>
            </Box>
        );
    };

    if (plans.length === 0) {
        return (
            <Paper
                elevation={2}
                sx={{
                    p: 5,
                    maxWidth: "700px",
                    mx: "auto",
                    borderRadius: 3,
                    bgcolor: "background.paper"
                }}
            >
                <Box sx={{ textAlign: "center", py: 3 }}>
                    <FitnessCenterIcon sx={{ fontSize: 60, color: "text.secondary", opacity: 0.7, mb: 2 }} />
                    <Typography variant="h5" fontWeight="medium" gutterBottom>
                        No plans available
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Currently there are no plans available for this affiliate. Please check back later or contact the gym directly for more information.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            <Fade in={true}>
                <Box>
                    {/* Render Special Price Standalone Plans First */}
                    {standalonePlans.specialPrice && standalonePlans.specialPrice.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Typography variant="h6" color="error.main" gutterBottom>
                                    🔥 Special Offers
                                </Typography>
                                <Divider sx={{ maxWidth: 200, mx: 'auto', borderColor: 'error.main' }} />
                            </Box>

                            <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
                                {standalonePlans.specialPrice.map((plan) => (
                                    <Grid item xs={12} sm={6} md={4} key={plan.id}>
                                        {renderPlanCard(plan)}
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {/* Render Gallery Groups */}
                    {galleryGroups.map((group) => renderCarousel(group))}

                    {/* Render Regular Standalone Plans */}
                    {standalonePlans.regular && standalonePlans.regular.length > 0 && (
                        <Box sx={{ mt: (galleryGroups.length > 0 || (standalonePlans.specialPrice && standalonePlans.specialPrice.length > 0)) ? 4 : 0 }}>
                            {(galleryGroups.length > 0 || (standalonePlans.specialPrice && standalonePlans.specialPrice.length > 0)) && (
                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    <Typography variant="h6" color="text.primary" gutterBottom>
                                        Individual Plans
                                    </Typography>
                                    <Divider sx={{ maxWidth: 200, mx: 'auto' }} />
                                </Box>
                            )}

                            <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
                                {standalonePlans.regular.map((plan) => (
                                    <Grid item xs={12} sm={6} md={4} key={plan.id}>
                                        {renderPlanCard(plan)}
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Fade>
        </Box>
    );
};

export default ShowPlans;